import {
  Button,
  Loader,
  MaterialIcons,
  MaterialUICore,
} from '@iclinic/design-system';
import { useFormikContext } from 'formik';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PrintSADTModal from 'features/tissInvoicing/containers/sadt/GuideSADT/ContainerFooterButtons/PrintSADTModal';
import { actions } from 'features/tissInvoicing/state/sadt';
import {
  getLoadingState,
  getPrintState,
} from 'features/tissInvoicing/state/sadt/selectors';
import { GuideSADTForm } from 'features/tissInvoicing/types';
import history from 'routes/history';
import { formKeys, FormKeysEnum } from '../validationSchema';
import * as Styles from './styles';

const { useMediaQuery, useTheme } = MaterialUICore;
const { ArrowBack, Save, Print } = MaterialIcons;

type GuideSADTKeys = keyof GuideSADTForm;
type GuideSADTSections = keyof typeof formKeys;
type THandleSubmit = React.MouseEvent<HTMLButtonElement> & { print: boolean };

interface ContainerFooterButtonsProps {
  sectionRef: React.RefObject<Array<HTMLButtonElement | null>>;
}

export default function ContainerFooterButtons({
  sectionRef,
}: ContainerFooterButtonsProps): JSX.Element {
  const classes = Styles.useStyles();
  const dispatch = useDispatch();

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('xs'));

  const loadingStatus = useSelector(getLoadingState);
  const { pdfURL, openModal } = useSelector(getPrintState);

  const { setFieldValue, validateForm } = useFormikContext<GuideSADTForm>();

  const closeModal = () => dispatch(actions.setPrintModalOpen(false));

  const notifySection = (section: string) => {
    dispatch(actions.setExpensesErrors(section === 'expensesData'));
  };

  const handleSubmit = (event: THandleSubmit): void => {
    const { print } = event;
    const scrollInto: HTMLButtonElement[] = [];

    setFieldValue('print', print, false);

    validateForm()
      .then((errors) => {
        Object.keys(formKeys).forEach((section) => {
          const hasError = formKeys[section as GuideSADTSections].some(
            (field) => !!errors[field as GuideSADTKeys],
          );

          const formKey = FormKeysEnum[section as keyof typeof FormKeysEnum];
          const element = sectionRef.current
            ? sectionRef.current[formKey]
            : null;

          if (hasError && element) {
            if (element.getAttribute('data-status') === 'closed')
              element.click();
            scrollInto.push(element);
            notifySection(section);
          }
        });

        if (scrollInto.length > 0)
          scrollInto[0].scrollIntoView({ behavior: 'smooth' });
      })
      .catch((err) => {
        throw new Error(`Erro ao validar formulário - ${String(err)}`);
      });

    if (scrollInto.length > 0)
      scrollInto[0].scrollIntoView({ behavior: 'smooth' });
  };

  const loading = useMemo(
    () => loadingStatus.print || loadingStatus.save,
    [loadingStatus.print, loadingStatus.save],
  );

  return (
    <Styles.Container>
      <Button
        onClick={() => history.goBack()}
        color="transparent"
        classes={{ root: classes.cancelButton }}
        startIcon={<ArrowBack />}
        disabled={loading}
      >
        Cancelar
      </Button>

      <Styles.Actions>
        <Button
          color={isSmall ? 'primary' : 'secondary'}
          type="submit"
          startIcon={loadingStatus.save ? <Loader size={15} /> : <Save />}
          disabled={loading}
          onClick={(e) => handleSubmit({ ...e, print: false })}
        >
          Salvar
        </Button>

        {!isSmall && (
          <Button
            type="submit"
            startIcon={loadingStatus.print ? <Loader size={15} /> : <Print />}
            disabled={loading}
            onClick={(e) => handleSubmit({ ...e, print: true })}
          >
            Imprimir
          </Button>
        )}
      </Styles.Actions>

      <PrintSADTModal
        isOpen={openModal}
        handleClose={closeModal}
        url={pdfURL}
      />
    </Styles.Container>
  );
}
